import { CommonModule } from '@angular/common';
import type { AfterViewInit, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Component, Inject, Input, PLATFORM_ID, QueryList, ViewChildren } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import type { CraftXGritProductEntry } from '@core-mkt/interfaces/craft-xgrit-product-entry';
import type { PageDataResponse } from '@core-mkt/resolvers/page-data/page-data-response';
import { CountdownService } from '@core-mkt/services/countdown/countdown.service';
import { CouponAggregationService } from '@core-mkt/services/coupon-aggregation/coupon-aggregation.service';
import { CouponQueryResolverService } from '@core-mkt/services/coupon-query-resolver/coupon-query-resolver.service';
import { CouponUseCases } from '@core-mkt/services/coupon-query-resolver/coupon-use-cases';
import { EnvService } from '@core-mkt/services/env/env.service';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import { PricingService } from '@core-mkt/services/pricing/pricing.service';
import { ProductService } from '@core-mkt/services/product/product.service';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import type { XGritCompleteProduct } from '@core-mkt/services/xgrit-api/xgrit-product';
import { TierBadgeComponent } from '@core-mkt/shared/components/tier-badge/tier-badge.component';
import type { VisionButton } from '@core-mkt/shared/components/vision-button/vision-button/vision-button';
import { VisionButtonComponent } from '@core-mkt/shared/components/vision-button/vision-button/vision-button.component';
import { CustomDecimalPipe } from '@ecom/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { VisionModalService } from '../../vision-modal/vision-modal.service';
import type { BundleItem, pricingTableItems } from './pricing-table';
import { pricingTable } from './pricing-table';
import { COUPON_SCROLL_TARGET } from '@core-mkt/services/coupon-scroll/coupon-scroll.service';
@Component({
  selector: 'pricingTable',
  templateUrl: './pricing-table.component.html',
  styleUrls: ['./pricing-table.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    VisionButtonComponent,
    LazyImgDirective,
    CustomDecimalPipe,
    QueryParamDirective,
    TierBadgeComponent,
  ],
})
export class PricingTableComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * Tailwind sticky top-offset classes used by the pricing-table header.
   * `top-10` = 2.5rem (40px) and matches the current graphic-banner rendered height.
   * Update this value if the shared banner/header spacing changes. See EPS-3771.
   */
  private static readonly STICKY_BANNER_HEIGHT = 'top-10';
  private static readonly NO_BANNER_HEIGHT = 'top-0';

  // FREC rule 61J2-17.013(1) prohibits exam-pass guarantees on FL real-estate products (EPS-1111);
  // guarantee text is hidden only when the brand AND the product state are both in these lists
  static readonly GUARANTEE_SUPPRESSED_BRANDS: readonly string[] = ['AA'];
  static readonly GUARANTEE_SUPPRESSED_STATES: readonly string[] = ['FL'];

  // BNPL (Buy Now Pay Later) calculation constants
  static readonly BNPL_THRESHOLD = 100; // Minimum price to show BNPL messaging
  private static readonly AFFIRM_INTEREST_RATE = 1.085; // 8.5% annual interest rate
  private static readonly AFFIRM_PAYMENT_MONTHS = 12; // 12-month payment plan
  private static readonly KLARNA_PAYMENT_INSTALLMENTS = 4; // 4 equal installments

  @Input() data: pricingTable;

  @ViewChildren('bundlePrice') bundlePrice: QueryList<ElementRef>;
  theme: ThemeInterface;
  couponApplied = false;
  activeCoupon = '';
  additionalCoupon: string; // Used to store additional coupon code from the cookie
  hasStickyBanner = false;
  private hideStickyBanner = false;
  closeIcon: SafeHtml | null;
  openIcon: SafeHtml | null;
  topContent: SafeHtml | null;
  urlCouponPresent: boolean[] = [];
  guaranteeTextOptions!: {
    moneyBack: {
      text: string;
      modalTitle: string;
      modalBody: string;
    };
    satisfactionGuarantee: {
      text: string;
      modalTitle: string;
      modalBody: string;
    };
  };

  globalCDIQueryParam: string;
  globalSalesQueryParam: string;
  public hasProducts = false;
  public hasBnplEligibleProducts = false;

  private destroy$ = new Subject<void>();

  constructor(
    private themeParser: ThemeParserService,
    private redactor: WysiwygRedactorService,
    private ps: ProductService,
    private pricingService: PricingService,
    private env: EnvService,
    private modalService: VisionModalService,
    private countdownService: CountdownService,
    private couponQueryResolverService: CouponQueryResolverService,
    private couponAggregationService: CouponAggregationService,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) protected platformId: object,
  ) {
    this.globalCDIQueryParam = this.couponQueryResolverService.setCouponQueryParam(CouponUseCases.CDI);
    this.globalSalesQueryParam = this.couponQueryResolverService.setCouponQueryParam(CouponUseCases.SALES);
    const resolverData = this.route.snapshot?.data?.pageData as PageDataResponse | undefined;
    this.hideStickyBanner = resolverData?.craftData?.headerData?.hideStickyBanner ?? false;
  }

  get bnplThreshold(): number {
    return PricingTableComponent.BNPL_THRESHOLD;
  }

  get bundles(): BundleItem[] {
    return this.data.bundles;
  }

  /** Marks this component as an email-capture scroll-back target. */
  readonly couponScrollTarget = COUPON_SCROLL_TARGET.pricingTable;

  /** A price has rendered, so this is a real email-capture scroll-back target (EPS-4801). */
  get hasPricedBundle(): boolean {
    return this.bundles?.some((b) => !!b.originalPrice) ?? false;
  }

  get vertical(): string {
    return this.env.get.brandConfig?.vertical?.replace('-', ' ');
  }

  get isAceDesign(): boolean {
    return (this.env.get.brandConfig.designMode ?? 'ace') === 'ace';
  }

  suppressGuarantee(product?: CraftXGritProductEntry): boolean {
    return (
      PricingTableComponent.GUARANTEE_SUPPRESSED_BRANDS.includes(this.env.get.brandConfig.id ?? '') &&
      PricingTableComponent.GUARANTEE_SUPPRESSED_STATES.includes(product?.productState ?? '')
    );
  }

  get guaranteeText(): string {
    return this.guaranteeTextOptions[this.data.guaranteeText as keyof typeof this.guaranteeTextOptions].text;
  }

  get guaranteeModalTitle(): string {
    return this.guaranteeTextOptions[this.data.guaranteeText as keyof typeof this.guaranteeTextOptions].modalTitle;
  }

  get guaranteeModalBody(): string {
    return this.guaranteeTextOptions[this.data.guaranteeText as keyof typeof this.guaranteeTextOptions].modalBody;
  }
  get bundlePricingItems(): pricingTableItems[] {
    return this.data.pricingTableItems;
  }
  get bundleTitleOne(): string {
    return this.data.bundleTitleOne ? this.data.bundleTitleOne : 'Basic';
  }
  get bundleTitleTwo(): string {
    return this.data.bundleTitleTwo ? this.data.bundleTitleTwo : 'Deluxe';
  }
  get bundleTitleThree(): string {
    return this.data.bundleTitleThree ? this.data.bundleTitleThree : 'Premium';
  }
  get bundleContainerStyles(): string {
    if (this.data.bundles.length > 2) {
      return 'lg:grid-cols-3 md:grid-cols-3';
    } else {
      return 'md:grid-cols-2 lg:grid-cols-2';
    }
  }
  get BottomBundleContainerStyles(): string {
    if (this.data.bundles.length > 2) {
      return 'lg:grid-cols-9';
    } else {
      return 'sm:grid-cols-6';
    }
  }
  get bundleContainerStylesGrid(): string {
    if (this.data.bundles.length > 2) {
      return 'lg:grid-cols-9 md:grid-cols-3 grid-cols-3';
    } else {
      return 'md:grid-cols-2 lg:grid-cols-3 grid-cols-2';
    }
  }
  get bundleStylesColsBig(): string {
    if (this.data.bundles.length > 2) {
      return 'lg:grid-cols-9 grid-cols-3';
    } else {
      return 'grid-cols-6 lg:grid-cols-6 md:grid-cols-6';
    }
  }
  get pricingTableRowPackage(): string {
    if (this.data.bundles.length > 2) {
      return 'col-span-1 lg:col-span-2';
    } else {
      return 'lg:col-span-2 col-span-3';
    }
  }
  get pricingTableRowPackageDissapear(): string {
    if (this.data.bundles.length > 2) {
      return 'lg:col-span-3 col-span-3';
    } else {
      return 'lg:col-span-2 md:col-span-2';
    }
  }
  get pricingTableRowLabel(): string {
    if (this.data.bundles.length > 2) {
      return 'lg:col-span-3 col-span-3';
    } else {
      return 'lg:col-span-2 col-span-6';
    }
  }
  get pricingTableRowPackageTable(): string {
    if (this.data.bundles.length > 2) {
      return 'lg:col-span-2';
    } else {
      return 'lg:col-span-2 col-span-3';
    }
  }
  get pricingTableRowPackageBottom(): string {
    if (this.data.bundles.length > 2) {
      return 'lg:col-span-2';
    } else {
      return 'lg:col-span-2 col-span-3';
    }
  }
  get bundleContainerStylesColSpan(): string {
    if (this.data.bundles.length > 2) {
      return 'lg:col-span-3 col-span-3';
    } else {
      return 'md:col-span-2';
    }
  }

  get stickyHeaderTopClass(): string {
    return this.hasStickyBanner ? PricingTableComponent.STICKY_BANNER_HEIGHT : PricingTableComponent.NO_BANNER_HEIGHT;
  }

  ngOnInit(): void {
    // Initialize guarantee text options
    this.guaranteeTextOptions = {
      moneyBack: {
        text: 'Pass the test or your money back!',
        modalTitle: `Ace or Don't Pay Guarantee`,
        modalBody: `Our online ${this.vertical} license course is so effective that we guarantee if you don't pass the licensing exam on your first try, we'll refund your money. Additional terms and conditions apply. State laws and regulations may prohibit some students from qualifying. Review our refund policy to learn more.`,
      },
      satisfactionGuarantee: {
        text: '30-Day Satisfaction Guarantee',
        modalTitle: `30-Day Satisfaction Guarantee!`,
        modalBody: `Try ${this.env.get.brandConfig.name} for 30 days. If you're not satisfied and haven't completed your course, we'll refund your money. Review our refund policy to learn more.`,
      },
    };

    this.data.guaranteeText = this.data.guaranteeText || 'moneyBack';
    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundColor: this.data.backgroundColor,
      backgroundImage: this.data.backgroundImage?.[0],
      textColor: '',
      columnCount: 0,
      columnDistribution: '',
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);
    this.topContent = this.redactor.bypassSanitizer(this.data.topContent);

    this.data.bundles.forEach((b) => {
      b.principalContent = this.redactor.bypassSanitizer(b.principalContent);
      b.bottomContent = this.redactor.bypassSanitizer(b.bottomContent);
    });

    // The comparison-table rows are authored WYSIWYG content and must go through the
    // redactor, exactly like the bundle fields above. Without this, Angular sanitises
    // the raw string on [innerHTML] binding and drops any element outside its built-in
    // allow-list — which unwrapped the EPS-4629 Wistia CTA <button> to its label <span>
    // and stripped the data-wistia-* payload the click handler needs.
    (this.data.pricingTableItems ?? []).forEach((item) => {
      item.principalContent = this.redactor.bypassSanitizer(item.principalContent);
    });

    // Check if a capture coupon already exists (user returning to page after email/phone capture)
    const couponCollection = this.couponAggregationService.collectAllCoupons();
    const existingCaptureCoupon = couponCollection?.coupons?.emailCapture || couponCollection?.coupons?.phoneCapture;

    if (existingCaptureCoupon?.trim()) {
      // Capture coupon exists from previous session - show "Coupon Applied!" badge
      this.couponApplied = true;
      this.additionalCoupon = existingCaptureCoupon;
    }

    this.pricingService.couponCodeEvent.pipe(takeUntil(this.destroy$)).subscribe((coupon: string | null) => {
      if (coupon && coupon.trim() !== '') {
        // Check if an email or phone capture coupon was just applied
        const couponCollection = this.couponAggregationService.collectAllCoupons();
        const captureCoupon = couponCollection?.coupons?.emailCapture || couponCollection?.coupons?.phoneCapture;

        if (captureCoupon?.trim()) {
          // Capture coupon found - use animated pricing for better UX
          this.additionalCoupon = captureCoupon;
          this.animateEmailCapturePrice();
        } else {
          // Standard coupon event handling
          this.activeCoupon = `&${this.globalCDIQueryParam}=` + coupon;
          this.additionalCoupon = coupon;
          this.adjustPricing(coupon);
        }
      }
    });

    this.countdownService.getSaleValue().subscribe((isSaleActive: boolean) => {
      this.hasStickyBanner = isSaleActive && !this.hideStickyBanner;
    });
  }

  ngAfterViewInit(): void {
    // Make single API call - CouponAggregationService automatically collects banner coupon
    // via getCurrentOverrideCoupon() or route data fallback
    this.getProducts(this.data.bundles);

    /**
     * Detect when ViewChildren bundlePrice changes and
     * adjust pricing if coupon is already applied
     */
    this.bundlePrice.changes.subscribe(() => {
      // 2nd condition prevent emailCapture coupon to override ongoing sale
      if (this.activeCoupon && this.urlCouponPresent.every((isPresent) => !isPresent)) {
        // If activeCoupon is already set, adjust pricing
        this.adjustPricing(this.additionalCoupon);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Builds a checkout URL ensuring all available coupons are consistently placed in the hc parameter
   */
  private buildCheckoutUrlWithAllCoupons(baseUrl: string): string {
    if (!baseUrl) return baseUrl;

    // Collect all available coupons from all sources
    const couponCollectionRequest = this.couponAggregationService.collectAllCoupons();
    const allAvailableCoupons = this.couponAggregationService.getAllCouponCodes(couponCollectionRequest);

    if (allAvailableCoupons.length === 0) {
      return baseUrl;
    }

    // Parse the URL to clean existing coupon parameters
    const urlObj = new URL(baseUrl);
    const params = new URLSearchParams(urlObj.search);

    // Remove any existing coupon parameters to avoid duplicates
    params.delete('hc');
    params.delete('vc');
    params.delete('coupon');
    params.delete('couponCode');

    // Add all coupons to the hc parameter (comma-separated)
    const couponString = allAvailableCoupons.join(',');
    params.set(this.globalCDIQueryParam, couponString);

    // Rebuild the URL
    urlObj.search = params.toString();
    return urlObj.toString();
  }

  /**
   * Animates pricing specifically for email capture coupons using CouponAggregationService
   */
  private animateEmailCapturePrice(): void {
    if (!this.data?.bundles) return;

    this.bundlePrice.forEach((el: ElementRef<HTMLDivElement>, index) => {
      const bundle = this.data.bundles[index];
      const product = this.ps.extractProduct(bundle.product);
      // ProductService uses CouponAggregationService to handle all coupons
      this.ps
        .getProductData(product)
        .then((product: XGritCompleteProduct) => {
          const start = product.apiData.originalPrice;
          const end = product.apiData.discountPrice;
          const maxPrice = product.apiData.maxPrice;
          const discountPrice = product.apiData.discountPrice;
          bundle.savePercentage = Math.round(((maxPrice - discountPrice) / maxPrice) * 100);
          bundle.maxPrice = maxPrice;
          bundle.originalPrice = discountPrice;
          bundle.discountType = product.apiData.discountType;

          bundle.affirmPrice = Math.ceil(
            discountPrice * (PricingTableComponent.AFFIRM_INTEREST_RATE / PricingTableComponent.AFFIRM_PAYMENT_MONTHS),
          );
          bundle.klarnaPrice = +(discountPrice / PricingTableComponent.KLARNA_PAYMENT_INSTALLMENTS).toFixed(2);

          this.couponApplied = true;
          if (start === end) return;
          let current = start;
          const priceDifference = start - end;
          const targetDuration = 2000; // 2 seconds
          const stepTime = targetDuration / priceDifference;
          const timer = setInterval(() => {
            current -= 1;
            el.nativeElement.innerHTML = '$' + current.toString();
            if (current <= end) {
              clearInterval(timer);
              return;
            }
          }, stepTime);
        })
        .catch(() => {
          // Email capture pricing animation failed - fallback to instant update
          this.getProducts(this.data.bundles);
        });
    });
  }

  public adjustPricing(coupon: string): void {
    if (!this.data?.bundles) {
      this.hasBnplEligibleProducts = false;
      return;
    }
    const promises: Promise<void>[] = [];
    this.bundlePrice.forEach((el, index) => {
      const bundle = this.data.bundles[index];
      const product = this.ps.extractProduct(bundle.product);

      product.xgritCoupon = coupon;
      const promise = this.ps
        .getProductData(product)
        .then((product: XGritCompleteProduct) => {
          const start = product.apiData.originalPrice;
          const end = product.apiData.discountPrice;
          const maxPrice = product.apiData.maxPrice;
          const discountPrice = product.apiData.discountPrice;

          bundle.savePercentage = Math.round(((maxPrice - discountPrice) / maxPrice) * 100);
          bundle.maxPrice = maxPrice;
          bundle.originalPrice = discountPrice;
          bundle.discountType = product.apiData.discountType;

          // Calculate payment options based on final discounted price
          bundle.affirmPrice = Math.ceil(
            discountPrice * (PricingTableComponent.AFFIRM_INTEREST_RATE / PricingTableComponent.AFFIRM_PAYMENT_MONTHS),
          );
          bundle.klarnaPrice = +(discountPrice / PricingTableComponent.KLARNA_PAYMENT_INSTALLMENTS).toFixed(2);

          this.couponApplied = true;
          if (start === end) {
            return;
          }
          let current = start;
          const priceDifference = start - end;
          const targetDuration = 2000; // 2 seconds
          const stepTime = targetDuration / priceDifference;
          const timer = setInterval(function () {
            current -= 1;
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands, @typescript-eslint/no-unsafe-member-access
            el.nativeElement.innerHTML = '$' + current;
            if (current <= end) {
              clearInterval(timer);
              return;
            }
          }, stepTime);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error adjusting pricing for index:', index, error);
        });
      promises.push(promise);
    });
    void Promise.allSettled(promises).then(() => {
      this.hasBnplEligibleProducts = this.data.bundles.some(
        (b) => b.originalPrice >= PricingTableComponent.BNPL_THRESHOLD,
      );
    });
  }

  activateModal(): void {
    const modalContent = `<div class="mr-1">${this.guaranteeModalTitle}</div>
    <div>
		${this.guaranteeModalBody}
	  </div>`;
    this.modalService.openModal({ modalContent: modalContent, showModal: true });
  }

  topContentStyles(topContnetStylesArray: string[]): string {
    return topContnetStylesArray.toString().replace(',', ' ');
  }

  getThemeForTopBanner(backgroundColor: string, textColor: string): ThemeInterface {
    if (backgroundColor === '') {
      const theme: ThemeInterface = {
        root: '',
        container: '',
        columnOrder: '',
        bgImage: '',
      };
      return theme;
    } else {
      const themeInput: ComponentTheme = {
        styles: [],
        backgroundColor: backgroundColor,
        backgroundImage: null,
        textColor: textColor,
        columnCount: 0,
        columnDistribution: '',
      };
      return this.themeParser.getThemeClasses(themeInput);
    }
  }

  getButtonData(bundleItem: BundleItem, type: string): VisionButton {
    // If using productCheckoutUrl (from API), it already has all coupons from API response
    // Only enhance custom ctaLink URLs that don't have API coupons
    let enhancedButtonLink: string;
    if (bundleItem.ctaLink) {
      // Custom link - needs coupon enhancement
      enhancedButtonLink = this.buildCheckoutUrlWithAllCoupons(bundleItem.ctaLink);
    } else {
      // API checkout URL already has all coupons - use as-is
      enhancedButtonLink = bundleItem.productCheckoutUrl ?? '';
    }

    if (type === 'bottom-bundle-small') {
      return {
        bgTheme: this.theme.root,
        buttonLink: enhancedButtonLink,
        buttonText: 'Enroll',
        buttonAriaLabel: bundleItem.ctaLinkAriaLabel ? bundleItem.ctaLinkAriaLabel : undefined,
        buttonStyle: bundleItem.ctaStyle,
        newTab: bundleItem.ctaLinkNewTab,
        nofollow: bundleItem.ctaLinkNofollow,
        noopener: bundleItem.ctaLinkNewTab,
        noreferrer: bundleItem.ctaLinkNewTab,
        fullWidth: true,
      };
    }
    return {
      bgTheme: this.theme.root,
      buttonLink: enhancedButtonLink,
      buttonText: bundleItem.ctaLabel || this.env.get.brandConfig.ctaText,
      buttonAriaLabel: bundleItem.ctaLinkAriaLabel ? bundleItem.ctaLinkAriaLabel : undefined,
      buttonStyle: bundleItem.ctaStyle,
      newTab: bundleItem.ctaLinkNewTab,
      nofollow: bundleItem.ctaLinkNofollow,
      noopener: bundleItem.ctaLinkNewTab,
      noreferrer: bundleItem.ctaLinkNewTab,
      fullWidth: true,
    };
  }

  /**
   * Sets the flag indicating whether products are available.
   * @param hasProducts - Boolean flag indicating if products exist
   */
  setHasProducts(hasProducts: boolean): void {
    this.hasProducts = hasProducts;
  }

  getProducts(bundles: BundleItem[]): void {
    const craftProducts: CraftXGritProductEntry[] = [];
    bundles.forEach((bundle) => {
      if (this.ps.isProductValid(bundle.product)) {
        craftProducts.push(this.ps.extractProduct(bundle.product));
      }
    });
    // If no valid products are found, exit early and set the `hasProducts` status to false
    if (craftProducts.length === 0) {
      this.setHasProducts(false);
      this.hasBnplEligibleProducts = false;
      return;
    } else {
      this.setHasProducts(true);
    }

    this.ps
      .getMultiProductData(craftProducts)
      .then((xgritProducts) => {
        if (xgritProducts.length !== 0) {
          bundles.forEach((bundle, index) => {
            const maxPrice = xgritProducts[index]?.apiData.maxPrice;
            const originalPrice = xgritProducts[index]?.apiData.originalPrice;
            const discountPrice = xgritProducts[index]?.apiData.discountPrice;
            bundle.productCheckoutUrl = xgritProducts[index]?.apiData.checkoutLink;
            bundle.discountType = xgritProducts[index]?.apiData.discountType;
            bundle.ctaLinkAriaLabel = xgritProducts[index]?.apiData.title;

            // Recording every time a product has a couponCode implying there's an ongoing sale
            this.urlCouponPresent.push(
              xgritProducts[index]?.apiData.checkoutLink && xgritProducts[index]?.craftData.xgritCoupon
                ? xgritProducts[index]?.apiData.checkoutLink.includes('couponCode') &&
                    xgritProducts[index]?.apiData.checkoutLink.includes(xgritProducts[index]?.craftData.xgritCoupon)
                : false,
            );
            if (
              xgritProducts[index]?.apiData.maxPrice &&
              xgritProducts[index]?.apiData.maxPrice !== xgritProducts[index]?.apiData.originalPrice
            ) {
              // Show Save % and both prices
              bundle.savePercentage = Math.round(((maxPrice - originalPrice) / maxPrice) * 100);
              bundle.maxPrice = maxPrice;
              bundle.originalPrice = originalPrice;
            } else if (
              xgritProducts[index]?.apiData.maxPrice &&
              xgritProducts[index]?.apiData.discountPrice &&
              xgritProducts[index]?.apiData.maxPrice === xgritProducts[index]?.apiData.originalPrice
            ) {
              // Show Save % and both prices
              bundle.savePercentage = Math.round(((maxPrice - discountPrice) / maxPrice) * 100);
              bundle.maxPrice = maxPrice;
              bundle.originalPrice = discountPrice;
            } else {
              // Only final price
              bundle.originalPrice = originalPrice;
            }
            // Use discountPrice to calculate monthly payments, as it is the final price after all discounts
            bundle.affirmPrice = Math.ceil(
              discountPrice *
                (PricingTableComponent.AFFIRM_INTEREST_RATE / PricingTableComponent.AFFIRM_PAYMENT_MONTHS),
            );
            bundle.klarnaPrice = +(discountPrice / PricingTableComponent.KLARNA_PAYMENT_INSTALLMENTS).toFixed(2);
          });
          this.hasBnplEligibleProducts = bundles.some((b) => b.originalPrice >= PricingTableComponent.BNPL_THRESHOLD);
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
        this.hasBnplEligibleProducts = false;
      });
  }

  scrollToPricingTable(): void {
    const element = document.getElementById('pricing-table-container');
    if (element) {
      const headerOffset = 55;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }
}
